import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Popup } from "devextreme-react";
import { Button } from "devextreme-react/button";
import ContextMenu from "devextreme-react/context-menu";
import { confirm } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import CustomerCredit from "../../../../../id/common/customer/CustomerCredit";

// Unique target id per button instance so each grid's context menu anchors to its own button.
let optionsInstanceCounter = 0;

// Tab identifiers that drive which options are visible / enabled.
// Mirrors the legacy uOMD0013 mgtTopTabChange logic.
export const OptionsTabType = {
    SalesOrder: "SalesOrder", 
    EdiWeb: "EdiWeb",         
    Order: "Order",           
    Customer: "Customer",     
    Item: "Item",             
};

const authHeaders = (accessToken) => ({
    access_token: accessToken,
    Authorization: `Bearer ${accessToken}`,
});

const SalesOrderOptionsButton = ({
    tabType = OptionsTabType.Order,
    customerId,
    selectedRowId,
    accessToken,
    onRefresh,
    disabled = false,
}) => {
    const [showCustomerCreditForm, setShowCustomerCreditForm] = useState(false);
    const [isApprover, setIsApprover] = useState(false);
    const buttonId = useRef(`so-options-btn-${++optionsInstanceCounter}`);

    // Legacy IsUserAnApprover: resolved once so the Approve Credit option can be disabled
    // for users without credit-approval rights.
    useEffect(() => {
        let active = true;
        if (tabType === OptionsTabType.SalesOrder) {
            axios
                .get(`${window._env_.REACT_APP_APIS}/id/Approver/IsCreditApprover`, {
                    headers: authHeaders(accessToken),
                })
                .then((res) => { if (active) setIsApprover(!!res.data?.isApprover); })
                .catch(() => { if (active) setIsApprover(false); });
        }
        return () => { active = false; };
    }, [tabType]);

    const items = [
        { id: "ShowCustomerCredit", name: "Show Customer Credit", disabled: tabType === OptionsTabType.Item },
        { id: "Delete", name: "Delete", disabled: tabType !== OptionsTabType.EdiWeb || !selectedRowId },
    ];
    if (tabType === OptionsTabType.SalesOrder) {
        items.push({ id: "ApproveCredit", name: "Approve Credit", disabled: !isApprover || !selectedRowId });
    }

    const handleDelete = async () => {
        if (!selectedRowId) return;
        const confirmed = await confirm("Are you sure you want to delete? Continue?", "Delete");
        if (!confirmed) return;
        try {
            // Reuse the existing OM delete-transaction endpoint (removes the header and its
            // details, matching the legacy uOMD0013 opDeleteClick). Body is the header rowid.
            const res = await axios.post(
                `${window._env_.REACT_APP_APIS}/om/transaction/DeleteTransaction`,
                selectedRowId,
                { headers: { ...authHeaders(accessToken), "Content-Type": "application/json" } }
            );
            const errors = res.data?.ERRORS || [];
            if (errors.length > 0) {
                notify({ message: errors.join(" "), width: 400 }, "error", 5000);
            } else {
                notify({ message: "Transaction deleted.", width: 400 }, "success", 3000);
                if (onRefresh) onRefresh();
            }
        } catch (e) {
            const errs = e?.response?.data?.ERRORS;
            const msg = (errs && errs.length) ? errs.join(" ") : "Delete failed.";
            notify({ message: msg, width: 400 }, "error", 5000);
        }
    };

    const handleApproveCredit = async () => {
        if (!selectedRowId) return;
        try {
            const res = await axios.post(
                `${window._env_.REACT_APP_APIS}/om/SaleOrder/ApproveCreditReview?rowId=${selectedRowId}`,
                {},
                { headers: authHeaders(accessToken) }
            );
            notify(
                { message: res.data?.message || (res.data?.Success ? "Credit review approved." : "Approval failed."), width: 400 },
                res.data?.Success ? "success" : "warning",
                3500
            );
            if (res.data?.Success && onRefresh) onRefresh();
        } catch (e) {
            notify({ message: "Approval failed.", width: 400 }, "error", 4000);
        }
    };

    const onItemClick = (e) => {
        const value = e.itemData?.id;
        if (value === "ShowCustomerCredit") {
            // Legacy opens the credit screen only when a customer row is selected.
            if (!customerId) return;
            setShowCustomerCreditForm(true);
        } else if (value === "Delete") {
            handleDelete();
        } else if (value === "ApproveCredit") {
            handleApproveCredit();
        }
    };

    return (
        <>
            <Button
                id={buttonId.current}
                className="gridTopButton"
                text="Options"
                type="default"
                disabled={disabled}
            />
            <ContextMenu
                target={`#${buttonId.current}`}
                showEvent="dxclick"
                items={items}
                displayExpr="name"
                keyExpr="id"
                onItemClick={onItemClick}
            />
            {showCustomerCreditForm && (
                <Popup
                    visible={showCustomerCreditForm}
                    onHiding={() => setShowCustomerCreditForm(false)}
                    width="90vw" 
                    height="90vh"
                    title="Customer Credit Status"
                    showCloseButton={true}
                >
                    <CustomerCredit customerId={customerId} module="OM" />
                </Popup>
            )}
        </>
    );
};

export default SalesOrderOptionsButton;
